import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const Vegetables = [
    "carrot.png", "tomato.png", "potato.png", "onion.png", "cabbage.png", "cauliflower.png", "broccoli.png", "spinach.png", "peas.png", "cucumber.png", "pumpkin.png", "brinjal.png", "radish.png", "capsicum.png", "corn.png", "bittergourd.png", "ladyfinger.png", "garlic.png", "mushroom.png", "beetroot.png"
];

const VegetablesNames = Vegetables.map(vegetable => vegetable.replace(".png", ""));

function shuffleArray<T>(array: T[]): T[] {
    return [...array].sort(() => Math.random() - 0.5);
}

type QuestionType = {
    text: string;
    correctAnimal: string;
    correctImage: string;
    allImages: string[];
};

const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    return { imageSize: screenWidth > 1024 ? 120 : screenWidth > 600 ? 100 : 80 };
};

const FindVegetable = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [imageSize, setImageSize] = useState(getDynamicSizes().imageSize);
    const [showImage, setShowImage] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        const handleResize = () => setImageSize(getDynamicSizes().imageSize);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const shuffledAnimals = shuffleArray([...Vegetables]);
        const correctAnimal = VegetablesNames[Math.floor(Math.random() * VegetablesNames.length)];
        const correctImage = `${correctAnimal}.png`;

        const incorrectImages = shuffledAnimals.filter(img => img !== correctImage).slice(0, remainingImages);
        const allImages = shuffleArray([correctImage, ...incorrectImages]);

        return {
            text: `Select the image of: ${correctAnimal.toUpperCase()}`,
            correctAnimal,
            correctImage,
            allImages,
        };
    };

    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;

        setTimeout(() => {
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20));
            const rows = Math.floor(container.height / (imageSize + 20));
            const totalImage = columns * rows;
            const remainingImages = totalImage > 1 ? totalImage - 1 : 0;

            setQuestion(generateRandomQuestion(remainingImages));
            setSelectedImage(null);
            setFeedback(null);
        }, 1000);
    }, [imageSize]);

    useEffect(() => {
        generateQuestion();
        document.title = "Find the Vegetable – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, [generateQuestion]);

    const handleImageClick = (image: string) => {
        if (!question) return;

        setSelectedImage(image);
        setFeedback(image === question.correctImage ? "Right" : "Wrong");

        if (image === question.correctImage) {
            setShowImage(true)
            setTimeout(() => {
                setSelectedImage(null);
                setFeedback(null);
                setQuestion(null);
                generateQuestion();
                setShowImage(false)
            }, 2000);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Make learning fun with the "Find the Vegetable" activity on Mamas Guidance—an interactive way for nursery kids to recognize and identify common vegetables, boosting their environmental awareness and observation skills.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord  match-container" style={{ marginTop: "0%" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Find the Vegetable</h1>

                                <p>
                                    Make learning fun and healthy with our <strong>Find the Vegetable</strong> worksheet! This simple and engaging EVS (Environmental Science) activity is designed for nursery kids to <strong>recognize and identify vegetables</strong> through colorful images and interactive questions.
                                </p>

                                <h2>🥕 What’s Inside the Worksheet?</h2>
                                <p>
                                    Children are shown pictures of various vegetables like <em>carrot, tomato, potato, spinach, brinjal</em>, and more. Their task is to <strong>find and circle or tick the correct vegetable</strong> based on a prompt such as:
                                </p>
                                <ul>
                                    <li><b>&quot;Find the carrot&quot;</b></li>
                                    <li><b>&quot;Which one is a tomato?&quot;</b></li>
                                    <li><b>&quot;Circle the green vegetable&quot;</b></li>
                                </ul>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Identify and name common vegetables</li>
                                    <li>Recognize vegetable colors, shapes, and types</li>
                                    <li>Improve observation and categorization skills</li>
                                    <li>Develop environmental and healthy eating awareness</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Say the vegetable name aloud or read the prompt to your child.</li>
                                    <li>Let them look through the pictures and find the correct vegetable.</li>
                                    <li>Ask them to circle, color, or tick the right image.</li>
                                    <li>Talk about where they’ve seen or eaten the vegetable before.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/environmental-science/animal/find-animal"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Animal</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/fruit/find-fruit"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Fruit</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Identify Correct Body Parts</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Valuable</h2>
                                <p>
                                    Early exposure to vegetables encourages familiarity and healthy habits. When children can <strong>name and recognize vegetables</strong>, they’re more likely to feel comfortable eating them. It also <strong>enhances vocabulary and awareness</strong> of the world around them.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Find the Vegetable</h1>

                            <p>
                                Make learning fun and healthy with our <strong>Find the Vegetable</strong> worksheet! This simple and engaging EVS (Environmental Science) activity is designed for nursery kids to <strong>recognize and identify vegetables</strong> through colorful images and interactive questions.
                            </p>

                            <h2>🥕 What’s Inside the Worksheet?</h2>
                            <p>
                                Children are shown pictures of various vegetables like <em>carrot, tomato, potato, spinach, brinjal</em>, and more. Their task is to <strong>find and circle or tick the correct vegetable</strong> based on a prompt such as:
                            </p>
                            <ul>
                                <li><b>&quot;Find the carrot&quot;</b></li>
                                <li><b>&quot;Which one is a tomato?&quot;</b></li>
                                <li><b>&quot;Circle the green vegetable&quot;</b></li>
                            </ul>

                            <h2>🎯 What Your Child Will Learn</h2>
                            <ul>
                                <li>Identify and name common vegetables</li>
                                <li>Recognize vegetable colors, shapes, and types</li>
                                <li>Improve observation and categorization skills</li>
                                <li>Develop environmental and healthy eating awareness</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Say the vegetable name aloud or read the prompt to your child.</li>
                                <li>Let them look through the pictures and find the correct vegetable.</li>
                                <li>Ask them to circle, color, or tick the right image.</li>
                                <li>Talk about where they’ve seen or eaten the vegetable before.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/environmental-science/animal/find-animal"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Animal</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/fruit/find-fruit"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Fruit</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Identify Correct Body Parts</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Valuable</h2>
                            <p>
                                Early exposure to vegetables encourages familiarity and healthy habits. When children can name and recognize vegetables, they’re more likely to feel comfortable eating them. It also enhances vocabulary and awareness of the world around them.
                            </p>

                        </article>
                        <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                        <div
                            ref={containerRef}
                            className="chooseImageContainer"
                            style={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                display: "grid",
                                gridTemplateColumns: `repeat(auto-fit, minmax(${imageSize}px, 1fr))`,
                                gap: "10px",
                                justifyContent: "center",
                                alignItems: "center",
                                overflow: "hidden"
                            }}
                        >
                            {question?.allImages.map((img, index) => (
                                <div key={index} style={{ textAlign: "center" }}>
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}${img}`}
                                        alt={img.replace(".png", "")}
                                        onClick={() => handleImageClick(img)}
                                        style={{
                                            width: `${imageSize}px`,
                                            height: `${imageSize}px`,
                                            cursor: "pointer",
                                            border: selectedImage === img ?
                                                (img === question.correctImage ? "5px solid green" : "5px solid red")
                                                : "none",
                                            transition: "transform 0.2s ease-in-out",
                                        }}
                                    />
                                    {selectedImage === img && (
                                        <div
                                            style={{
                                                fontWeight: "bold",
                                                color: feedback === "Right" ? "green" : "red",
                                                marginTop: "5px",
                                            }}
                                        >
                                            {feedback}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: '150px' }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                            alt="Well Done"
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default FindVegetable;
